import { create } from 'zustand';
import { clearPersistedQueryCache, setPersisterUser } from '@/lib/queryPersister';
import type { AppUser } from '@/types';
import type { Session } from '@supabase/supabase-js';

interface AppState {
  user: AppUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  isDemo: boolean;
  currentConsultor: string;
  currentConsultorId: string | null; // auth.users UUID — for DB filtering
  sidebarOpen: boolean;
  companionOpen: boolean;
  theme: 'light' | 'dark';

  // Frete toggle (false = líquido, true = com frete)
  showFrete: boolean;
  toggleFrete: () => void;

  // Spectator Mode
  spectatorMode: boolean;
  spectatorConsultor: string | null;
  realUser: AppUser | null;

  setUser: (user: AppUser | null) => void;
  setSession: (session: Session | null) => void;
  setConsultor: (consultor: string, consultorId?: string | null) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleCompanion: () => void;
  setCompanionOpen: (open: boolean) => void;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  login: (user: AppUser, session: Session) => void;
  logout: () => void;
  enterSpectatorMode: (consultorNome: string) => void;
  exitSpectatorMode: () => void;
}

const savedTheme = (localStorage.getItem('crm-theme') as 'light' | 'dark') || 'dark';

// TODO: Para sincronizar tema entre dispositivos, adicionar coluna jsonb `preferencias`
// na tabela colaboradores e salvar/ler { tema: 'light'|'dark' } no login/toggleTheme.
// Migration necessária: ALTER TABLE colaboradores ADD COLUMN preferencias jsonb DEFAULT '{}';

// Restore user from localStorage if available
function getSavedUser(): AppUser | null {
  try {
    const raw = localStorage.getItem('crm-user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
const restoredUser = getSavedUser();

// 08/jul: impersonação diretoria "ver como" (sessão real). A flag sobrevive a
// reload/fechamento de aba — reidrata o banner e a trava de só-leitura.
const impersonActive = (() => { try { return localStorage.getItem('araunah-imperson-active'); } catch { return null; } })();

export const useAppStore = create<AppState>((set, get) => ({
  user: restoredUser,
  session: null,
  isAuthenticated: !!restoredUser,
  isDemo: false,
  currentConsultor: restoredUser
    ? (restoredUser.role === 'diretor' || restoredUser.role === 'gestor_agro' || restoredUser.role === 'relacionamento' ? '__ALL__' : restoredUser.nome)
    : '__ALL__',
  currentConsultorId: restoredUser
    ? (restoredUser.role === 'diretor' || restoredUser.role === 'gestor_agro' || restoredUser.role === 'relacionamento' ? null : restoredUser.id)
    : null,
  // 05/06: lembra a escolha do desktop (barra encolhida fica encolhida ao recarregar)
  sidebarOpen: typeof window !== 'undefined' ? (window.innerWidth >= 768 ? localStorage.getItem('crm-sidebar-collapsed') !== '1' : false) : true,
  companionOpen: false,
  theme: savedTheme,

  // Frete toggle
  showFrete: false, // 08/10: sem botão c/ frete; régua oficial = NF líquida, frete pago em card próprio no Cockpit
  toggleFrete: () => set((s) => ({ showFrete: !s.showFrete })),

  // Spectator Mode (impersonação por sessão real — ver impersonate.ts)
  spectatorMode: !!impersonActive,
  spectatorConsultor: impersonActive,
  realUser: null,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      currentConsultor:
        user?.role === 'diretor' || user?.role === 'gestor_agro' || user?.role === 'relacionamento'
          ? '__ALL__'
          : user?.nome ?? '__ALL__',
      currentConsultorId:
        user?.role === 'diretor' || user?.role === 'gestor_agro' || user?.role === 'relacionamento'
          ? null
          : user?.id ?? null,
    }),

  setSession: (session) => set({ session }),

  setConsultor: (consultor, consultorId) => set({ currentConsultor: consultor, currentConsultorId: consultorId ?? null }),

  toggleSidebar: () => set((s) => {
    const v = !s.sidebarOpen;
    try {
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        localStorage.setItem('crm-sidebar-collapsed', v ? '0' : '1');
      }
    } catch { /* quota */ }
    return { sidebarOpen: v };
  }),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  toggleCompanion: () => set((s) => ({ companionOpen: !s.companionOpen })),

  setCompanionOpen: (open) => set({ companionOpen: open }),

  toggleTheme: () =>
    set((s) => {
      const next = s.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('crm-theme', next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      return { theme: next };
    }),

  setTheme: (theme) => {
    localStorage.setItem('crm-theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
    set({ theme });
  },

  login: (user, session) => {
    localStorage.setItem('crm-user', JSON.stringify(user));
    // 01/07: cache offline é assinado por usuário — reassina o buster do
    // persister pro dono real da sessão (ver queryPersister.setPersisterUser).
    setPersisterUser(user.id);
    set({
      user,
      session,
      isAuthenticated: true,
      currentConsultor:
        user.role === 'diretor' || user.role === 'gestor_agro' || user.role === 'relacionamento'
          ? '__ALL__'
          : user.nome,
      currentConsultorId:
        user.role === 'diretor' || user.role === 'gestor_agro' || user.role === 'relacionamento'
          ? null
          : user.id,
    });
  },

  logout: () => {
    localStorage.removeItem('crm-user');
    // 01/07: aparelho compartilhado — o cache offline (IndexedDB crm-rq*) e o
    // cache do service worker (api-cache-v2) guardam respostas RLS-filtradas do
    // usuário que saiu; sem limpar, o próximo login neste aparelho restauraria
    // leads/pedidos dele (indefinidamente se offline). Best-effort assíncrono.
    void clearPersistedQueryCache();
    setPersisterUser(null);
    if (typeof caches !== 'undefined') {
      caches.delete('api-cache-v2').catch(() => { /* SW cache indisponível */ });
    }
    set({
      user: null,
      session: null,
      isAuthenticated: false,
      currentConsultor: '__ALL__',
      currentConsultorId: null,
      spectatorMode: false,
      spectatorConsultor: null,
      realUser: null,
    });
  },

  enterSpectatorMode: (consultorNome: string) => {
    const { user } = get();
    if (!user) return;
    const syntheticUser: AppUser = {
      id: user.id,
      email: user.email,
      nome: consultorNome,
      role: 'consultor',
      consultor: user.consultor,
    };
    set({
      realUser: user,
      spectatorMode: true,
      spectatorConsultor: consultorNome,
      user: syntheticUser,
      currentConsultor: consultorNome,
    });
  },

  exitSpectatorMode: () => {
    const { realUser } = get();
    if (!realUser) return;
    set({
      user: realUser,
      spectatorMode: false,
      spectatorConsultor: null,
      realUser: null,
      currentConsultor: '__ALL__',
    });
  },
}));
